/**
 * *Save N measurements to the property* — `docs/property-maps-v2.md` §5.4,
 * board 5 tile 5.
 *
 * **Every save is reviewed, both of them.** *Add to property measurements* on
 * one shape and *Save to property* on everything waiting open this same
 * dialog, because the thing being written is a number a quote will be built
 * on and the person pressing the button is standing in a map editor, not on
 * the Overview card where it lands. So the dialog says what each row will be
 * called, what it measures, and whether it is new.
 *
 * **A clash is asked about, never guessed.** `onClash: 'ask'` writes nothing
 * and comes back naming the rows (§4.3), so this re-opens with Replace or
 * Keep both on exactly those rows and the rest untouched. Writing eight of
 * nine and asking afterwards is the outcome nobody can reason about.
 */
import { useState } from 'react'
import { ApiError } from '@rowted/shared'
import { Button } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { cn } from '../../lib/cn.js'
import { formatNumber } from '../../lib/format.js'
import { IconRuler } from '../../frame/icons.js'
import { saveParts, type Clash, type PartBody } from '../api.js'
import { typeColour } from './rows.js'

/** One row of the review, with everything it needs to draw and to send. */
export interface SaveRow extends PartBody {
  /** The measure shape this came from, so the caller can mark it saved. */
  shapeId?: string
  typeName: string
  unit: string
}

export function SaveMeasurementsDialog({
  propertyId, rows, who, onCancel, onSaved,
}: {
  propertyId: string
  rows: SaveRow[]
  /** `A. Kline`, for the footer's source line. */
  who: string | null
  onCancel: () => void
  onSaved: (saved: SaveRow[]) => void
}) {
  const [clashes, setClashes] = useState<Clash[] | null>(null)
  /** What to do with each clashing row, by index. Unset until asked. */
  const [choice, setChoice] = useState<Record<number, 'replace' | 'both'>>({})
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const decided = clashes === null
    || clashes.every((clash) => choice[clash.index] !== undefined)

  async function save() {
    setBusy(true)
    setProblem(null)
    try {
      /*
       * One `onClash` for the call, which is what the endpoint takes — so a
       * mixed answer is two calls, each carrying its own rows. Two
       * transactions rather than one is the honest cost of letting somebody
       * replace one name and keep both of another.
       */
      const groupsOf = (want: 'replace' | 'both'): SaveRow[] => rows.filter(
        (_row, index) => choice[index] === want)
      const clean = rows.filter((_row, index) => choice[index] === undefined)

      if (clashes === null) {
        await saveParts(propertyId, { parts: rows.map(toBody), onClash: 'ask' })
      } else {
        if (clean.length > 0) {
          await saveParts(propertyId, { parts: clean.map(toBody), onClash: 'ask' })
        }
        for (const want of ['replace', 'both'] as const) {
          const some = groupsOf(want)
          if (some.length > 0) {
            await saveParts(propertyId, { parts: some.map(toBody), onClash: want })
          }
        }
      }
      onSaved(rows)
    } catch (cause: unknown) {
      const found = cause instanceof ApiError
        && (cause.details as { code?: string } | undefined)?.code === 'name_clash'
        ? (cause.details as { clashes?: Clash[] }).clashes ?? []
        : null
      if (found) {
        setClashes(found)
        setProblem(null)
      } else {
        setProblem(cause instanceof Error ? cause.message : 'Those could not be saved.')
      }
    } finally {
      setBusy(false)
    }
  }

  const title = `Save ${rows.length} measurement${rows.length === 1 ? '' : 's'} to the property`

  return (
    <SheetOrDialog
      onClose={onCancel}
      title={title}
      width={560}
      mark={<IconRuler size={16} />}
      footerNote={`Source: Aerial${who ? ` · ${who}` : ''} · today`}
      footer={(
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" disabled={busy || !decided} onClick={() => void save()}>
            {busy ? 'Saving…' : `Save ${rows.length}`}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div className="overflow-hidden rounded-field border border-line">
          {rows.map((row, index) => {
            const clash = clashes?.find((one) => one.index === index)
            return (
              <div
                key={row.shapeId ?? `${row.typeId}:${row.name}`}
                className={cn(
                  'grid items-center gap-2.5 border-b border-line-faint px-3.5 py-2.5',
                  'grid-cols-[10px_minmax(0,1fr)_110px_auto] text-body last:border-b-0',
                )}
              >
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-tag"
                  style={{ background: typeColour(row.typeName) }}
                />
                <div className="flex min-w-0 flex-col gap-px">
                  <b className="truncate font-semibold">{row.name}</b>
                  <span className="truncate text-fieldlabel text-text-muted">
                    {`${row.typeName} · Aerial · today`}
                  </span>
                </div>
                <b className="whitespace-nowrap text-right tabular-nums">
                  {`${formatNumber(Math.round(row.value))} ${row.unit}`}
                </b>
                <span className="flex justify-end">
                  {clash
                    ? (
                      <span className="flex gap-1">
                        <ClashButton
                          label="Replace"
                          on={choice[index] === 'replace'}
                          onClick={() => setChoice((was) => ({ ...was, [index]: 'replace' }))}
                        />
                        <ClashButton
                          label="Keep both"
                          on={choice[index] === 'both'}
                          onClick={() => setChoice((was) => ({ ...was, [index]: 'both' }))}
                        />
                      </span>
                    )
                    : (
                      <span className={cn(
                        'inline-flex h-[19px] items-center rounded-tag bg-accent-soft px-1.5',
                        'text-sub font-semibold text-accent-dark',
                      )}
                      >
                        New
                      </span>
                    )}
                </span>
              </div>
            )
          })}
        </div>

        <div className="flex flex-col gap-1.5 text-cell leading-relaxed text-text-body">
          <span>
            {'They’ll appear in '}
            <b>Overview › About › Measured</b>
            {' and are ready to pick when you build a quote line.'}
          </span>
          <span className={clashes ? 'font-semibold text-warn' : 'text-text-muted'}>
            {clashes
              ? (clashes.length === 1
                ? 'One of these names is already measured here.'
                : `${clashes.length} of these names are already measured here.`)
                + ' Pick Replace or Keep both on each.'
              : 'If a measurement with the same name and type is already on file, you’ll choose'
                + ' Replace or Keep both.'}
          </span>
          {problem && <span className="font-semibold text-danger">{problem}</span>}
        </div>
      </div>
    </SheetOrDialog>
  )
}

const toBody = (row: SaveRow): PartBody => ({
  name: row.name,
  typeId: row.typeId,
  value: row.value,
  source: row.source,
  ...(row.featureId ? { featureId: row.featureId } : {}),
  ...(row.measuredOn ? { measuredOn: row.measuredOn } : {}),
})

function ClashButton({ label, on, onClick }: {
  label: string; on: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'inline-flex h-[22px] items-center rounded-tag border px-1.5 text-sub font-semibold',
        on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line text-text-muted',
      )}
    >
      {label}
    </button>
  )
}
